import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';

import { AlertPosition } from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import { AlertService } from '@valk-nx/components/ui-alert/src/lib/services/alert.service';
import { BFF_URL, WITH_CREDENTIALS } from '@valk-nx/core/lib/core';
import { HALResponse } from '@valk-nx/core/lib/interfaces/hal.interface';
import { StoreHelper } from '@valk-nx/helpers/lib/store/store.helper';

import { AvailabilityDatesInterface } from './availability-dates.interface';

@Injectable({ providedIn: 'root' })
export class AvailabilityDatesService {
	private readonly alertService = inject(AlertService);
	private readonly translate = inject(TranslateService);
	private readonly http = inject(HttpClient);
	private readonly availabilityUrl = inject(BFF_URL);
	private readonly withCredentials = inject<{
		withCredentials: boolean;
	}>(WITH_CREDENTIALS);

	getAvailabilityDatesRaw(
		hotelGUID: string,
		endDate: string,
		startDate: string,
		occupancies: { adults: number; children: number; infants: number }[],
		accommodationTypes: string[],
		dealGUID?: string,
		packageGUID?: string,
		roomGUID?: string,
		nights?: number,
		promotionGUID?: string,
	) {
		const headers = new HttpHeaders();

		const qParams = StoreHelper.createQueryParamString({
			accommodationTypes:
				accommodationTypes && accommodationTypes.length > 0
					? accommodationTypes
					: undefined,
			endDate,
			hotelGUID,
			occupancies,
			startDate,
			dealGUID,
			packageGUID,
			roomGUID,
			nights,
			promotionGUID,
		});
		return this.http
			.get<HALResponse<AvailabilityDatesInterface>>(
				`${this.availabilityUrl}availability/dates${qParams}`,
				{
					...this.withCredentials,
					headers,
					observe: 'body',
					responseType: 'json',
				},
			)
			.pipe(map((response) => response.data));
	}

	getAvailabilityDates(
		hotelGUID: string,
		endDate: string,
		startDate: string,
		occupancies: { adults: number; children: number; infants: number }[],
		accommodationTypes: string[],
		dealGUID?: string,
		packageGUID?: string,
		roomGUID?: string,
		nights?: number,
		promotionGUID?: string,
	) {
		return this.getAvailabilityDatesRaw(
			hotelGUID,
			endDate,
			startDate,
			occupancies,
			accommodationTypes,
			dealGUID,
			packageGUID,
			roomGUID,
			nights,
			promotionGUID,
		).pipe(
			catchError(() => {
				const content = this.translate.instant(
					'availability-widget.retrieve-availability-error',
				);
				this.alertService.error({
					closeable: true,
					duration: 5000,
					emphasized: false,
					hasElevation: true,
					position: AlertPosition.TopCenter,
					content,
				});
				return of({});
			}),
		);
	}
}
